import { computed, inject, Injectable, Injector } from '@angular/core';

import { WcgFilterIndicatorsOptions } from '@shared/interfaces/wcg-table-utils.interface';
import {
  BaseGridViewState,
  BASE_GRID_VIEW_STATE,
} from '@data-access/extendables';
import {
  SubmissionListSignalState,
  SubmissionListItem,
  BatchAssignmentsPayload,
  AssignmentPayload,
  QuickFilterKey,
} from '@data-access/submissions/types';
import { updateGridFilter } from '@data-access/shared';
import { WcgAdvancedFilter, WcgMenuOption } from '@wcg/frontend-framework';
import { mapSmeQcUserToMenuOption } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class SubmissionListFacadeService extends BaseGridViewState {
  private readonly _submissionGridStore =
    inject<SubmissionListSignalState>(BASE_GRID_VIEW_STATE);
  private readonly _injector = inject(Injector);

  readonly $submissions = computed(() =>
    this._submissionGridStore.$submissions(),
  );
  readonly $currentUrl = computed(() =>
    this._submissionGridStore.$currentUrl(),
  );

  readonly $submissionListScope = computed(() =>
    this._submissionGridStore.$submissionListScope(),
  );

  readonly $isUpdatingSMEQC = computed(() =>
    this._submissionGridStore.$isUpdatingSMEQC(),
  );

  readonly $isAllSelected = computed(() =>
    this._submissionGridStore.$isAllSelected(),
  );
  readonly $isIndeterminate = computed(() =>
    this._submissionGridStore.$isIndeterminate(),
  );
  readonly $selectedRows = computed(() =>
    this._submissionGridStore.$selectedRows(),
  );
  readonly $isInternal = computed(() =>
    this._submissionGridStore.$isInternal(),
  );

  readonly $quickFilterCounts = computed(() =>
    this._submissionGridStore.$quickFilterCounts(),
  );

  readonly $quickFilter = computed(() =>
    this._submissionGridStore.$quickFilter(),
  );

  readonly $smeList = computed(() => this._submissionGridStore.$smeList());
  readonly $qcList = computed(() => this._submissionGridStore.$qcList());

  readonly $smeUserListOptions = computed<WcgMenuOption[]>(() => {
    const smeList = this.$smeList() ?? [];

    return smeList.map((user) =>
      mapSmeQcUserToMenuOption(user, this._injector),
    );
  });

  readonly $qcUserListOptions = computed<WcgMenuOption[]>(() => {
    const qcList = this.$qcList() ?? [];

    return qcList.map((user) => mapSmeQcUserToMenuOption(user, this._injector));
  });

  /**
   * NOTE: We are going forward with using inject() instead of constructor injection
   * But Super class needs to have the parameters passed in constructor
   * which we cannot do in the parent class for this specific signal store.
   * Hence, we are using inject() here and passing the values to super()
   */
  constructor() {
    super();
  }

  clearSearchString(): void {
    this._submissionGridStore.updateSearchString('');
  }

  /**
   * Updates `$advancedFilters` state **without** triggering an HTTP request.
   *
   * Use this when the data has already been fetched with the correct filter
   * (e.g. via the resolver) and only the UI chip / indicator state needs to
   * reflect the active selection.
   *
   * @param filters - Flattened advanced-filter map to persist in the store.
   */
  patchAdvancedFilters(filters: WcgAdvancedFilter): void {
    this._submissionGridStore.updateAdvancedFilters(filters);
  }

  updateSubmissionGridFilter(filter: WcgFilterIndicatorsOptions) {
    const _constructFilter = updateGridFilter(
      filter,
      this._submissionGridStore.$advancedFilters(),
    );
    this.updateGrid(
      'filter',
      {
        filters: _constructFilter.filters,
        page: this._submissionGridStore.$pageView().page,
        pageSize: this._submissionGridStore.$pageView().pageSize,
      },
      true,
      false,
    );
  }

  toggleRow(row: SubmissionListItem, checked: boolean) {
    this._submissionGridStore.selectRow(row, checked);
  }

  toggleSelectAll(checked: boolean) {
    this._submissionGridStore.selectAll(checked);
  }

  resetSelectedRows(): void {
    this._submissionGridStore.resetSelectedRows();
  }

  batchUpdateWorkspaceSMEQC(assignments: BatchAssignmentsPayload): void {
    this._submissionGridStore.batchUpdateWorkspaceSMEQC(assignments);
  }

  batchRemoveSMEQC(assignments: BatchAssignmentsPayload): void {
    this._submissionGridStore.batchRemoveSMEQC(assignments);
  }

  updateWorkspaceSMEQC(assignment: AssignmentPayload): void {
    this._submissionGridStore.updateWorkspaceSMEQC(assignment);
  }

  removeWorkspaceSMEQC(assignment: AssignmentPayload): void {
    this._submissionGridStore.removeWorkspaceSMEQC(assignment);
  }

  reloadSubmissionList(updateColumns?: boolean): void {
    this._submissionGridStore.reloadSubmissionList(updateColumns);
  }

  /** Fetches quick-filter bucket counts for the current list scope. */
  loadQuickFilterCounts(): void {
    this._submissionGridStore.loadQuickFilterCounts();
  }

  /** Sets the active quick filter; applied to every subsequent list load. */
  setQuickFilter(key: QuickFilterKey): void {
    this._submissionGridStore.setQuickFilter(key);
  }
}
